import { Input } from "@/components/ui/input";
import {
	formatMessage,
	getRuleDisplayLabel,
	getRuleDisplayName,
	reconciliationResultMessages,
} from "@/services/messages";
import { Pin, Search } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useRef } from "react";
import { extractCodeFromGroupKey } from "./lib/group-key";
import { getCardDelta, getCardTitle } from "./lib/master-display";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Master-pane навигатор для master-detail layout (фича 024 ③).
 *
 * Слева на странице с большим числом расхождений (≥15 — авто-триггер).
 * Содержит: header с counters, search input, list групп с item-rows.
 * Pinned-расхождения выделяются в отдельную виртуальную группу сверху.
 */
export interface DiscrepancyMasterProps {
	/** Уже отфильтрованные (без muted) карточки. Master не делает повторной фильтрации. */
	cards: DiscrepancyCardViewModel[];
	activeId: string | null;
	onActiveChange: (id: string) => void;
	pinnedIds: ReadonlySet<string>;
	onTogglePin: (id: string) => void;
	search: string;
	onSearchChange: (value: string) => void;
	/** Внешний ref на search input, чтобы хук `useDiscrepancyKbd` мог вызвать .focus(). */
	searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

interface MasterGroup {
	name: string | null;
	pinned?: boolean;
	items: DiscrepancyCardViewModel[];
}

export function DiscrepancyMaster({
	cards,
	activeId,
	onActiveChange,
	pinnedIds,
	onTogglePin,
	search,
	onSearchChange,
	searchInputRef,
}: DiscrepancyMasterProps) {
	const messages = reconciliationResultMessages.redesign;

	// Debounce поиска — useDeferredValue даёт «soft» отложенный пересчёт без таймеров (M19).
	const deferredSearch = useDeferredValue(search);

	const filtered = useMemo(() => {
		const q = deferredSearch.trim().toLowerCase();
		if (!q) return cards;
		return cards.filter((c) => {
			const code = c.groupKey.toLowerCase();
			const summary = c.summary.toLowerCase();
			const rules = c.violations
				.flatMap((v) => [v.ruleId.toLowerCase(), getRuleDisplayName(v.ruleId).toLowerCase()])
				.join(" ");
			return code.includes(q) || summary.includes(q) || rules.includes(q);
		});
	}, [cards, deferredSearch]);

	const groups: MasterGroup[] = useMemo(() => {
		const result: MasterGroup[] = [];
		const pinned = filtered.filter((c) => pinnedIds.has(c.id));
		if (pinned.length > 0) {
			result.push({ name: messages.master_pinned_group_label, pinned: true, items: pinned });
		}
		const rest = filtered.filter((c) => !pinnedIds.has(c.id));
		if (rest.length > 0) result.push({ name: null, items: rest });
		return result;
	}, [filtered, pinnedIds, messages.master_pinned_group_label]);

	const errorCount = cards.filter((c) => c.severity === "error").length;
	const warningCount = cards.length - errorCount;

	return (
		// h-full обязателен: grid-item с `max-h` на контейнере не shrink'ается к
		// row-height без явной высоты, и `<ul flex-1 overflow-y-auto>` ниже не
		// получает constraint → не скроллится при N>visible items.
		<div className="flex h-full min-h-0 flex-col border-slate-200 border-r">
			<header className="sticky top-0 z-10 border-slate-200 border-b bg-white px-3 pt-3 pb-2">
				<div className="mb-2 flex items-center justify-between">
					<span className="font-semibold text-[11px] text-slate-700 uppercase tracking-wide">
						{formatMessage(messages.master_count_template, { n: cards.length })}
					</span>
					<div className="flex items-center gap-2 font-mono text-[11px]">
						<span className="inline-flex items-center gap-1">
							<span className="h-1.5 w-1.5 rounded-full bg-red-500" />
							<span className="text-slate-700">{errorCount}</span>
						</span>
						<span className="inline-flex items-center gap-1">
							<span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
							<span className="text-slate-700">{warningCount}</span>
						</span>
					</div>
				</div>
				<div className="relative">
					<Search
						className="-translate-y-1/2 absolute top-1/2 left-2 h-3.5 w-3.5 text-slate-400"
						aria-hidden="true"
					/>
					<Input
						ref={searchInputRef}
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder={messages.master_search_placeholder}
						className="h-8 bg-slate-50 px-7 py-1.5 text-[12px]"
					/>
					<kbd className="-translate-y-1/2 absolute top-1/2 right-2 rounded bg-slate-100 px-1 font-mono text-[10px] text-slate-500">
						/
					</kbd>
				</div>
				<div className="mt-2 flex items-center justify-end text-[11px] text-slate-500">
					<span>{messages.master_keyboard_hint}</span>
				</div>
			</header>
			<ul className="min-h-0 flex-1 overflow-y-auto">
				{groups.length === 0 ? (
					<li className="px-3 py-6 text-center text-[12px] text-slate-500">
						{messages.master_empty_filter}
					</li>
				) : null}
				{groups.map((g) => (
					<MasterGroupSection
						key={g.name ?? "_ungrouped"}
						group={g}
						activeId={activeId}
						onActiveChange={onActiveChange}
						pinnedIds={pinnedIds}
						onTogglePin={onTogglePin}
					/>
				))}
			</ul>
		</div>
	);
}

interface MasterGroupSectionProps {
	group: MasterGroup;
	activeId: string | null;
	onActiveChange: (id: string) => void;
	pinnedIds: ReadonlySet<string>;
	onTogglePin: (id: string) => void;
}

function MasterGroupSection({
	group,
	activeId,
	onActiveChange,
	pinnedIds,
	onTogglePin,
}: MasterGroupSectionProps) {
	return (
		<>
			{group.name ? (
				<li
					className={`sticky top-0 border-slate-100 border-b px-3 py-1 font-semibold text-[10px] uppercase tracking-wide ${
						group.pinned ? "z-[2] bg-amber-50 text-amber-800" : "z-[1] bg-slate-50 text-slate-500"
					}`}
				>
					{group.name} <span className="font-mono text-slate-400">({group.items.length})</span>
				</li>
			) : null}
			{group.items.map((item) => (
				<MasterItemRow
					key={item.id}
					card={item}
					active={activeId === item.id}
					pinned={pinnedIds.has(item.id)}
					onActiveChange={onActiveChange}
					onTogglePin={onTogglePin}
				/>
			))}
		</>
	);
}

interface MasterItemRowProps {
	card: DiscrepancyCardViewModel;
	active: boolean;
	pinned: boolean;
	onActiveChange: (id: string) => void;
	onTogglePin: (id: string) => void;
}

function MasterItemRow({ card, active, pinned, onActiveChange, onTogglePin }: MasterItemRowProps) {
	const messages = reconciliationResultMessages.redesign;
	const sevDot = card.severity === "error" ? "bg-red-500" : "bg-yellow-500";
	const ruleIds = Array.from(new Set(card.violations.map((v) => v.ruleId)));
	const primaryRule = ruleIds[0] ?? "";
	const ruleLabel = primaryRule ? getRuleDisplayLabel(primaryRule) : "";
	const code = extractCodeFromGroupKey(card.groupKey);
	const sourcesCount = card.actSources.length + card.workOrderSources.length;
	const filesCount = new Set([
		...card.actSources.map((s) => s.fileId),
		...card.workOrderSources.map((s) => s.fileId),
	]).size;
	const title = getCardTitle(card);
	const delta = getCardDelta(card);

	// Sprint 6 / C1.2: при keyboard-навигации (J/K) активная row может оказаться
	// вне viewport master-pane — авто-scroll внутрь. `block: 'nearest'` не
	// дёргает scroll если row уже видим.
	const rowRef = useRef<HTMLLIElement>(null);
	useEffect(() => {
		if (active) rowRef.current?.scrollIntoView({ block: "nearest" });
	}, [active]);

	return (
		<li ref={rowRef} className="relative border-slate-100 border-b">
			{/* Pin-кнопка вынесена из row-button и позиционирована абсолютно над ним:
			    избегаем nested-button (invalid HTML), сохраняя семантику обеих кнопок. */}
			<button
				type="button"
				onClick={() => onActiveChange(card.id)}
				className={`relative w-full px-3 py-2 pr-9 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${active ? "bg-blue-50/70" : "hover:bg-slate-50"}`}
				aria-current={active ? "true" : undefined}
			>
				{active ? (
					<span className="absolute top-0 bottom-0 left-0 w-0.5 bg-blue-500" aria-hidden="true" />
				) : null}
				<div className="flex items-start gap-2">
					<span
						className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${sevDot}`}
						aria-hidden="true"
					/>
					<div className="min-w-0 flex-1">
						<div className="flex items-baseline gap-1.5">
							<span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 uppercase tracking-wide">
								{ruleLabel}
								{ruleIds.length > 1 ? <span className="ml-0.5">×{ruleIds.length}</span> : null}
							</span>
							{code ? <span className="font-mono text-[11px] text-slate-500">{code}</span> : null}
						</div>
						<p className="mt-0.5 truncate font-medium text-[12.5px] text-slate-900">{title}</p>
						<div className="mt-1 flex items-center gap-2">
							{delta ? (
								<span
									className={`font-mono rounded px-1.5 py-0.5 text-[10.5px] ring-1 ring-inset ${
										delta.tone === "red"
											? "bg-red-50 text-red-700 ring-red-200"
											: "bg-yellow-50 text-yellow-800 ring-yellow-200"
									}`}
								>
									{delta.label}
								</span>
							) : null}
							<span className="text-[10.5px] text-slate-400">
								{formatMessage(messages.master_sources_template, {
									sources: sourcesCount,
									files: filesCount,
								})}
							</span>
						</div>
					</div>
				</div>
			</button>
			<button
				type="button"
				onClick={() => onTogglePin(card.id)}
				aria-label={pinned ? messages.detail_unpin_aria : messages.detail_pin_aria}
				className={`absolute top-2.5 right-2.5 rounded p-0.5 ${pinned ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}
			>
				<Pin className="h-3 w-3" aria-hidden="true" />
			</button>
		</li>
	);
}
